import React, { createContext, ReactNode, useContext, useState } from 'react';
import { authenticate } from '~/services/authService';
import { AUTH_TOKEN_KEY } from '~/constants';
import { number } from 'yup';

interface User {
  id: string | number;
  email: string;
  avatarURL: string;
  firstName: string;
  lastName: string;
}

interface AuthContextType {
  user?: User;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  loginError?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginError, setLoginError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const setAuthData = (
    user: User,
    token: string,
    refreshToken: string,
    expires?: number,
    rememberMe?: boolean
  ) => {
    const expiresAt = expires ?? Date.now() + 3600 * 1000; // Default to 1 hour in ms

    setUser(user);
    setIsAuthenticated(true);
    if (rememberMe) {
      sessionStorage.setItem(AUTH_TOKEN_KEY, token); // Save the token to storage

      localStorage.setItem(
        'auth',
        JSON.stringify({ token, refreshToken, expiresAt, user })
      );
    } else {
      sessionStorage.setItem(AUTH_TOKEN_KEY, token); // Save the token to storage

      sessionStorage.setItem(
        'auth',
        JSON.stringify({ token, refreshToken, expiresAt, user })
      );
    }
  };

  const clearAuthData = () => {
    setUser(undefined);
    setIsAuthenticated(false);
    localStorage.removeItem('auth');
    sessionStorage.removeItem('auth');
  };

  React.useEffect(() => {
    const loadAuth = async () => {
      const authData =
        sessionStorage.getItem('auth') ?? localStorage.getItem('auth');

      if (authData) {
        try {
          const { token, refreshToken, expiresAt, user } = JSON.parse(authData);
          console.log({ token, refreshToken, expiresAt, user });
          if (Date.now() - 35 * 60 * 1000 < expiresAt) {
            setAuthData(user, token, refreshToken);
          } else if (refreshToken) {
            console.log({
              expiresAt,
              expired: true,
              now: Date.now(),
              diff: Date.now() - expiresAt,
            });
            // Token expired, try refreshing
            // try {
            //   const {
            //     user: newUser,
            //     token: newToken,
            //     refreshToken: newRefreshToken,
            //   } = await apiRefreshToken(refreshToken);
            //   setAuthData(newUser, newToken, newRefreshToken);
            // } catch (error) {
            //   console.error('Refresh failed:', error);
            //   clearAuthData();
            // }
          } else {
            // No refresh token, clear auth
            clearAuthData();
          }
        } catch {
          clearAuthData();
        }
      }
      setIsLoading(false);
    };
    loadAuth().then();
  }, []);

  const login = async (
    email: string,
    password: string,
    rememberMe?: boolean
  ) => {
    try {
      setLoginError(undefined);

      const response = await authenticate({ email, password });

      if (response.user.id) {
        setAuthData(
          {
            id: response.user.id,
            firstName: response.user.firstName,
            lastName: response.user.lastName,
            email: response.user.email,
            avatarURL: response.user.avatarURL,
          },
          response.token,
          response.token
        );

        return true;
      }
    } catch {
      /* empty */
    }

    setLoginError('something went wrong');

    return false;
  };

  const logout = () => {
    setUser(undefined);
    setIsAuthenticated(false);
    setLoginError(undefined);
  };

  return (
    <AuthContext.Provider
      value={{ isLoading, user, isAuthenticated, login, logout, loginError }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
